import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../ThemeContext';

// Simple number formatting for memory (MB to GB) and integers
function formatBytesMB(mb?: number | null) {
  if (mb == null) return '-';
  const gb = mb / 1024;
  return gb >= 1 ? `${gb.toFixed(1)} GB` : `${mb} MB`;
}

function formatInt(n?: number | null) {
  return n == null ? '-' : n.toLocaleString();
}

export default function SparkPage() {
  const { theme } = useTheme();
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Provisioning state
  const [provisioningStatus, setProvisioningStatus] = useState<any | null>(null);
  const [provisioningError, setProvisioningError] = useState<string | null>(null);
  
  // Scaling controls state
  const [scalingInProgress, setScalingInProgress] = useState<boolean>(false);
  const [workerCount, setWorkerCount] = useState<number>(1);
  const [masterCpu, setMasterCpu] = useState<number>(4.0);
  const [masterMemory, setMasterMemory] = useState<number>(16.0);
  const [workerCpu, setWorkerCpu] = useState<number>(4.0);
  const [workerMemory, setWorkerMemory] = useState<number>(16.0);

  const sparkUiUrl = useMemo(() => {
    // Allow opening the raw UI if reachable from browser
    const host = (import.meta as any)?.env?.VITE_SPARK_MASTER_HOST || 'localhost';
    const port = (import.meta as any)?.env?.VITE_SPARK_MASTER_WEBUI_PORT || '8080';
    return `http://${host}:${port}`;
  }, []);

  const fetchData = async () => {
    try {
      setError(null);
      const resp = await fetch('/api/spark/cluster');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setData(json);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const fetchProvisioningStatus = async () => {
    try {
      setProvisioningError(null);
      const resp = await fetch('/api/spark/status');
      if (!resp.ok) {
        if (resp.status === 400) {
          // Running in local mode without Azure
          setProvisioningStatus({ localMode: true });
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }
      const json = await resp.json();
      setProvisioningStatus(json);
    } catch (e: any) {
      setProvisioningError(e?.message || 'Failed to load provisioning status');
      setProvisioningStatus(null);
    }
  };

  const handleProvision = async () => {
    if (scalingInProgress) return;
    
    setScalingInProgress(true);
    setProvisioningError(null);
    
    try {
      const resp = await fetch('/api/spark/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          master_cpu: masterCpu,
          master_memory_gb: masterMemory,
          worker_count: workerCount,
          worker_cpu: workerCpu,
          worker_memory_gb: workerMemory,
        }),
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${resp.status}`);
      }
      
      const result = await resp.json();
      alert(`Success: ${result.message}`);
      
      // Refresh status after provisioning
      await fetchProvisioningStatus();
      await fetchData();
    } catch (e: any) {
      setProvisioningError(e?.message || 'Failed to provision cluster');
      alert(`Provisioning failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setScalingInProgress(false);
    }
  };

  const handleDeprovision = async () => {
    if (scalingInProgress) return;
    
    if (!window.confirm('Are you sure you want to deprovision the entire Spark cluster? All running jobs will be terminated.')) {
      return;
    }
    
    setScalingInProgress(true);
    setProvisioningError(null);
    
    try {
      const resp = await fetch('/api/spark/deprovision', {
        method: 'DELETE',
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${resp.status}`);
      }
      
      const result = await resp.json();
      alert(`Success: ${result.message}`);
      
      // Refresh status after deprovisioning
      await fetchProvisioningStatus();
      await fetchData();
    } catch (e: any) {
      setProvisioningError(e?.message || 'Failed to deprovision cluster');
      alert(`Deprovisioning failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setScalingInProgress(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchProvisioningStatus();
    const i = setInterval(() => {
      fetchData();
      fetchProvisioningStatus();
    }, 10000); // refresh every 10s
    return () => clearInterval(i);
  }, []);

  const totalWorkers = data?.workers?.length || 0;
  const aliveWorkers = data?.aliveWorkers ?? data?.workers?.filter((w: any) => w.state === 'ALIVE')?.length ?? 0;

  return (
    <div style={{ background: theme.cardBackground, borderRadius: 12, padding: 16, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, color: theme.text }}>Spark Cluster</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={fetchData}
            style={{
              background: theme.buttonBackground,
              color: theme.buttonText,
              padding: '8px 12px',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Refresh
          </button>
          <button
            onClick={() => window.open(sparkUiUrl, '_blank', 'noopener,noreferrer')}
            style={{
              background: theme.buttonBackground,
              color: theme.buttonText,
              padding: '8px 12px',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Open Spark UI
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ color: theme.textSecondary }}>Loading cluster info…</div>
      )}

      {error && (
        <div style={{ background: theme.errorLight, border: `1px solid ${theme.error}`, color: theme.errorDark, padding: 12, borderRadius: 8, marginBottom: 12 }}>
          Failed to load cluster info: Cluster not provisioned yet.
        </div>
      )}

      {/* Provisioning Status Section */}
      {provisioningStatus && !provisioningStatus.localMode && (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 12px 0', color: theme.text }}>Cluster Provisioning Status (Azure ACI)</h3>
          
          {provisioningError && (
            <div style={{ background: theme.errorLight, border: `1px solid ${theme.error}`, color: theme.errorDark, padding: 12, borderRadius: 8, marginBottom: 12 }}>
              {provisioningError}
            </div>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
            <StatCard 
              label="Master Status" 
              value={provisioningStatus.master ? provisioningStatus.master.provisioning_state || 'Unknown' : 'Not Provisioned'} 
              theme={theme} 
            />
            <StatCard 
              label="Workers Provisioned" 
              value={provisioningStatus.worker_count || 0} 
              theme={theme} 
            />
            <StatCard 
              label="Cluster Ready" 
              value={provisioningStatus.cluster_ready ? 'Yes' : 'No'} 
              theme={theme} 
            />
          </div>
          
          {provisioningStatus.master && (
            <div style={{ marginBottom: 12, padding: 12, background: theme.hoverBackground, borderRadius: 8 }}>
              <div style={{ color: theme.text, fontWeight: 600, marginBottom: 8 }}>Master Details:</div>
              <div style={{ color: theme.textSecondary, fontSize: 13 }}>
                FQDN: {provisioningStatus.master.fqdn || 'N/A'}<br />
                IP: {provisioningStatus.master.ip_address || 'N/A'}<br />
                State: {provisioningStatus.master.provisioning_state || 'Unknown'}
              </div>
            </div>
          )}
          
          {provisioningStatus.workers && provisioningStatus.workers.length > 0 && (
            <div style={{ marginBottom: 12, padding: 12, background: theme.hoverBackground, borderRadius: 8 }}>
              <div style={{ color: theme.text, fontWeight: 600, marginBottom: 8 }}>Workers:</div>
              {provisioningStatus.workers.map((worker: any, idx: number) => (
                <div key={idx} style={{ color: theme.textSecondary, fontSize: 13, marginBottom: 4 }}>
                  Worker {idx + 1}: {worker.name} - {worker.provisioning_state || 'Unknown'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scaling Controls Section */}
      <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px 0', color: theme.text }}>Manual Scaling Controls</h3>
        
        {provisioningStatus?.localMode && (
          <div style={{ background: theme.hoverBackground, padding: 12, borderRadius: 8, marginBottom: 12, color: theme.textSecondary }}>
            Running in local/docker mode. Azure provisioning not available.
          </div>
        )}
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 16 }}>
          {/* Master Configuration */}
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: theme.text, fontSize: 14 }}>Master Configuration</h4>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>CPU Cores</label>
              <input
                type="number"
                value={masterCpu}
                onChange={(e) => setMasterCpu(parseFloat(e.target.value))}
                step="0.5"
                min="0.5"
                max="8"
                disabled={scalingInProgress || provisioningStatus?.localMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  color: theme.text,
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>Memory (GB)</label>
              <input
                type="number"
                value={masterMemory}
                onChange={(e) => setMasterMemory(parseFloat(e.target.value))}
                step="0.5"
                min="1"
                max="16"
                disabled={scalingInProgress || provisioningStatus?.localMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  color: theme.text,
                }}
              />
            </div>
          </div>
          
          {/* Worker Configuration */}
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: theme.text, fontSize: 14 }}>Worker Configuration</h4>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>Worker Count</label>
              <input
                type="number"
                value={workerCount}
                onChange={(e) => setWorkerCount(parseInt(e.target.value))}
                min="0"
                max="10"
                disabled={scalingInProgress || provisioningStatus?.localMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  color: theme.text,
                }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>CPU per Worker</label>
              <input
                type="number"
                value={workerCpu}
                onChange={(e) => setWorkerCpu(parseFloat(e.target.value))}
                step="0.5"
                min="1"
                max="8"
                disabled={scalingInProgress || provisioningStatus?.localMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  color: theme.text,
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>Memory per Worker (GB)</label>
              <input
                type="number"
                value={workerMemory}
                onChange={(e) => setWorkerMemory(parseFloat(e.target.value))}
                step="0.5"
                min="2"
                max="16"
                disabled={scalingInProgress || provisioningStatus?.localMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 4,
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  color: theme.text,
                }}
              />
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={handleProvision}
            disabled={scalingInProgress || provisioningStatus?.localMode}
            style={{
              background: scalingInProgress || provisioningStatus?.localMode ? theme.border : theme.successLight,
              color: scalingInProgress || provisioningStatus?.localMode ? theme.textSecondary : theme.successDark,
              padding: '10px 16px',
              border: 'none',
              borderRadius: 8,
              cursor: scalingInProgress || provisioningStatus?.localMode ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              flex: 1,
            }}
          >
            {scalingInProgress ? 'Processing...' : 'Provision Cluster'}
          </button>
          <button
            onClick={handleDeprovision}
            disabled={scalingInProgress || provisioningStatus?.localMode}
            style={{
              background: scalingInProgress || provisioningStatus?.localMode ? theme.border : theme.errorLight,
              color: scalingInProgress || provisioningStatus?.localMode ? theme.textSecondary : theme.errorDark,
              padding: '10px 16px',
              border: 'none',
              borderRadius: 8,
              cursor: scalingInProgress || provisioningStatus?.localMode ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              flex: 1,
            }}
          >
            {scalingInProgress ? 'Processing...' : 'Deprovision Cluster'}
          </button>
        </div>
      </div>

      {data && (
        <>
          {/* Cluster Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
            <StatCard label="Status" value={data.status || 'Unknown'} theme={theme} />
            <StatCard label="Workers (alive/total)" value={`${aliveWorkers}/${totalWorkers}`} theme={theme} />
            <StatCard label="Cores (used/total)" value={`${formatInt(data.coresUsed)}/${formatInt(data.cores)}`} theme={theme} />
            <StatCard label="Memory (used/total)" value={`${formatBytesMB(data.memoryUsed)}/${formatBytesMB(data.memory)}`} theme={theme} />
          </div>

          {/* Workers */}
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: '8px 0 12px 0', color: theme.text }}>Workers</h3>
            <div style={{ overflow: 'auto', border: `1px solid ${theme.border}`, borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: theme.hoverBackground }}>
                  <tr>
                    <Th theme={theme}>ID</Th>
                    <Th theme={theme}>Host</Th>
                    <Th theme={theme}>State</Th>
                    <Th theme={theme}>Cores</Th>
                    <Th theme={theme}>Memory</Th>
                    <Th theme={theme}>Executors</Th>
                    <Th theme={theme}>Last Heartbeat</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.workers?.map((w: any) => (
                    <tr key={w.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                      <Td mono theme={theme}>{w.id}</Td>
                      <Td theme={theme}>{w.host}:{w.port}</Td>
                      <Td theme={theme}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 9999,
                          background: w.state === 'ALIVE' ? theme.successLight : theme.errorLight,
                          color: w.state === 'ALIVE' ? theme.successDark : theme.errorDark,
                          fontWeight: 600,
                          fontSize: 12
                        }}>{w.state}</span>
                      </Td>
                      <Td theme={theme}>{formatInt(w.cores)}</Td>
                      <Td theme={theme}>{formatBytesMB(w.memory)}</Td>
                      <Td theme={theme}>{formatInt(w.coresUsed)} / {formatBytesMB(w.memoryUsed)}</Td>
                      <Td theme={theme}>{w.lastHeartbeat ? new Date(w.lastHeartbeat).toLocaleString() : '-'}</Td>
                    </tr>
                  ))}
                  {(!data.workers || data.workers.length === 0) && (
                    <tr>
                      <Td colSpan={7} theme={theme}>
                        <div style={{ color: theme.textSecondary, textAlign: 'center' }}>No workers reported by master.</div>
                      </Td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Applications */}
          {data.applications && (
            <div>
              <h3 style={{ margin: '8px 0 12px 0', color: theme.text }}>Applications</h3>
              
              {/* Running Applications */}
              {data.applications.running && data.applications.running.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px 0', color: theme.text, fontSize: 14 }}>
                    Running Applications ({data.applications.runningCount})
                  </h4>
                  <div style={{ overflow: 'auto', border: `1px solid ${theme.border}`, borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: theme.hoverBackground }}>
                        <tr>
                          <Th theme={theme}>ID</Th>
                          <Th theme={theme}>Name</Th>
                          <Th theme={theme}>Started</Th>
                          <Th theme={theme}>Duration</Th>
                          <Th theme={theme}>Cores</Th>
                          <Th theme={theme}>Memory</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.applications.running.map((app: any) => (
                          <tr key={app.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                            <Td mono theme={theme}>{app.id}</Td>
                            <Td theme={theme}>{app.name}</Td>
                            <Td theme={theme}>{app.starttime ? new Date(app.starttime).toLocaleString() : '-'}</Td>
                            <Td theme={theme}>{app.duration ? `${Math.floor(app.duration / 1000)}s` : '-'}</Td>
                            <Td theme={theme}>{formatInt(app.cores)}</Td>
                            <Td theme={theme}>{formatBytesMB(app.memoryperslave)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Completed Applications */}
              {data.applications.completed && data.applications.completed.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', color: theme.text, fontSize: 14 }}>
                    Completed Applications ({data.applications.completedCount})
                  </h4>
                  <div style={{ overflow: 'auto', border: `1px solid ${theme.border}`, borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: theme.hoverBackground }}>
                        <tr>
                          <Th theme={theme}>ID</Th>
                          <Th theme={theme}>Name</Th>
                          <Th theme={theme}>Started</Th>
                          <Th theme={theme}>Completed</Th>
                          <Th theme={theme}>Duration</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.applications.completed.map((app: any) => (
                          <tr key={app.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                            <Td mono theme={theme}>{app.id}</Td>
                            <Td theme={theme}>{app.name}</Td>
                            <Td theme={theme}>{app.starttime ? new Date(app.starttime).toLocaleString() : '-'}</Td>
                            <Td theme={theme}>{app.endtime ? new Date(app.endtime).toLocaleString() : '-'}</Td>
                            <Td theme={theme}>{app.duration ? `${Math.floor(app.duration / 1000)}s` : '-'}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(!data.applications.running || data.applications.running.length === 0) && 
               (!data.applications.completed || data.applications.completed.length === 0) && (
                <div style={{ color: theme.textSecondary, textAlign: 'center', padding: 12 }}>
                  No applications found.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, theme }: { label: string, value: React.ReactNode, theme: any }) {
  return (
    <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12 }}>
      <div style={{ color: theme.textSecondary, fontSize: 12 }}>{label}</div>
      <div style={{ color: theme.text, fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function Th({ children, theme }: { children: React.ReactNode, theme: any }) {
  return (
    <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.textSecondary, fontWeight: 600, fontSize: 13 }}>{children}</th>
  );
}

function Td({ children, mono, colSpan, theme }: { children: React.ReactNode, mono?: boolean, colSpan?: number, theme: any }) {
  return (
    <td colSpan={colSpan} style={{ padding: '10px 12px', color: theme.text, fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' : undefined }}>{children}</td>
  );
}
